/**
 * Twilio Voice SDK bridge for real browser Agent audio.
 *
 * This deliberately does NOT drive its own call UI. Konnextions already has
 * one call state machine — the server, mirrored by useAgentWorkspace and
 * rendered by CallBar/AgentWorkspace — and this hook's only job is to make
 * sure the Device silently answers the specific Twilio leg the SERVER
 * created for whatever the existing UI already decided (the agent clicking
 * Answer triggers POST /agent/calls/{id}/answer, which is what makes Twilio
 * ring this Device with an "incoming" call in the first place). There is no
 * separate incoming-call UI here on purpose: showing one would let an agent
 * accept audio without the platform's own state machine agreeing a call is
 * actually being answered.
 */

import { useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import { api } from '@/lib/api/client';

export type TwilioDeviceStatus = 'idle' | 'connecting' | 'ready' | 'error';

interface TokenResponse {
  token: string;
  identity: string;
  ttl_seconds: number;
}

export interface TwilioDeviceState {
  status: TwilioDeviceStatus;
  error: string | null;
}

/**
 * @param enabled Only register a Device while true — an org not using
 *   Twilio, or an agent whose endpoint mode isn't twilio_client, must never
 *   have this fetch a token or request microphone access.
 */
export function useTwilioDevice(enabled: boolean): TwilioDeviceState {
  const [state, setState] = useState<TwilioDeviceState>({ status: 'idle', error: null });
  const deviceRef = useRef<Device | null>(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let cancelled = false;
    setState({ status: 'connecting', error: null });

    const setup = async () => {
      try {
        const { token } = await api.get<TokenResponse>('/agent/twilio/token');
        if (cancelled) return;

        const device = new Device(token, { logLevel: 'error' });
        deviceRef.current = device;

        device.on('registered', () => {
          if (!cancelled) setState({ status: 'ready', error: null });
        });
        device.on('error', (twilioError: { message?: string }) => {
          if (!cancelled) {
            setState({ status: 'error', error: twilioError.message ?? 'Twilio Device error' });
          }
        });
        // The server created this leg because the existing call-control UI
        // already decided to answer — accept immediately, silently. This is
        // the audio connect, not a user-facing prompt.
        device.on('incoming', (call) => {
          call.accept();
        });
        device.on('tokenWillExpire', async () => {
          try {
            const refreshed = await api.get<TokenResponse>('/agent/twilio/token');
            device.updateToken(refreshed.token);
          } catch {
            // A failed refresh surfaces naturally as a registration error on
            // the next expiry-triggered reconnect attempt; nothing to do
            // here that wouldn't duplicate that.
          }
        });

        await device.register();
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            error: error instanceof Error ? error.message : 'Could not start browser calling',
          });
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      deviceRef.current?.destroy();
      deviceRef.current = null;
    };
  }, [enabled]);

  return state;
}
